<template>
  <footer>
    <div class="bg-black text-white">
      <div class="py-6 sm:py-16 px-4 container mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-12">
          <div v-for="{ children, id, title } in footerMenu" :key="id" class="hidden md:block">
            <h4 class="text-lg font-semibold mb-4">
              {{ title }}
            </h4>
            <ol>
              <li v-for="child in children" :key="child.id" class="hover:underline cursor-pointer mb-4 last:mb-0">
                <AtomLink :url="child.url">{{ child.title }}</AtomLink>
              </li>
            </ol>
          </div>
          <div class="md:hidden">
            <ol class="columns-2 gap-x-8">
              <template v-for="{ children } in footerMenu">
                <li v-for="{ id, title, url } in children" :key="id" class="block text-sm hover:underline cursor-pointer mb-2">
                  <AtomLink :url="url">{{ title }}</AtomLink>
                </li>
              </template>
            </ol>
          </div>
          <div class="flex flex-col">
            <h4 class="text-lg font-semibold mb-4 order-2 md:order-1 hidden md:block">
              {{ t_('follow-us') }}
            </h4>
            <div class="flex items-center justify-between md:justify-start order-3 md:order-2">
              <template v-for="{ icon, url, label } in socialMedia" :key="url">
                <AtomLink :url="url" class="group" :aria-label="label">
                  <AtomIcon
                    :icon="icon"
                    class="w-8 h-8 md:w-6 mr-5 md:mb-8 mb-5 group-hover:text-app-primary-1-orange--bittersweet transform transition duration-200"
                    :title="label"
                  />
                </AtomLink>
              </template>
            </div>
            <div v-if="newsletterMenu.length" class="hidden md:inline-flex order-3 shrink-0">
              <MoleculeButton
                :url="newsletterMenu[0]?.url"
                :enable-icon-left="false"
                background-color="bg-white hover:bg-app-primary-1-red--fire-brick"
                text-color="text-black hover:text-white"
                size="medium"
              >
                {{ t_('newsletter') }}
                <AtomIconMail class="w-4 h-4 ml-3" />
              </MoleculeButton>
            </div>

            <MoleculeButton
              v-if="newsletterMenu.length"
              :url="newsletterMenu[0]?.url"
              :enable-icon-left="false"
              background-color="bg-white hover:bg-app-primary-1-red--fire-brick"
              text-color="text-black hover:text-white"
              class="flex md:hidden order-1 mb-6"
            >
              {{ t_('newsletter') }}
              <AtomIconMail class="w-4 h-4 ml-3" />
            </MoleculeButton>

            <div class="md:hidden order-4 text-sm mb-4">
              {{ t_('sound-fragment') }}
              <AtomLink url="https://www.classicstogo.nl/" class="underline">
                {{ t_('classics-to-go') }}
              </AtomLink>
            </div>
            <ol class="flex flex-row order-5 md:mb-0 md:hidden text-sm">
              <li v-for="{ id, title, url } in bottomMenu" :key="id" role="menuitem" class="inline-block mr-16 last:mr-0">
                <AtomLink :url="url">
                  {{ title }}
                </AtomLink>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-white">
      <div
        class="pt-4 px-4 pb-20 xl:pb-4 container mx-auto text-sm flex flex-col md:flex-row md:grid md:grid-cols-2 lg:flex md:gap-4 2xl:gap-16 md:items-center lg:justify-between"
      >
        <h5 class="mb-4 md:mb-0 hidden md:block">
          {{ t_('sound-fragment') }}
          <AtomLink url="https://www.classicstogo.nl/" class="underline">
            {{ t_('classics-to-go') }}
          </AtomLink>
        </h5>
        <ol class="hidden md:flex gap-10 lg:gap-5 flex-col md:flex-row justify-end lg:px-8 2xl:gap-12">
          <li v-for="{ id, title, url } in bottomMenu" :key="id" role="menuitem" class="inline-block last:mr-0">
            <AtomLink :url="url">
              {{ title }}
            </AtomLink>
          </li>
        </ol>
        <div
          class="md:col-span-2 grid gap-2 grid-cols-2 sm:flex justify-items-center items-center sm:justify-center lg:justify-end sm:gap-12 lg:gap-4 xl:gap-10 2xl:gap-16 2xl:flex-auto"
        >
          <AtomLink url="/contact-het-concertgebouw-fonds/" class="md:inline-block shrink-0">
            <AtomLogosHetConcertgebouwFonds class="h-12 lg:h-10 xl:h-12" :alt="logoT_('het-concertgebouw-fonds')" />
          </AtomLink>
          <AtomLink url="https://www.vriendenloterij.nl/" target="_blank" class="md:inline-block shrink-0">
            <img class="h-[48px] lg:h-10 xl:h-[48px] w-[90px] lg:w-auto xl:w-[90px]" src="/images/logos/VL_LOGO_2024_RGB.png" :alt="logoT_('vrienden-loterij')" />
          </AtomLink>
          <AtomLink url="https://www.vanlanschotkempen.com/" target="_blank" class="md:inline-block shrink-0">
            <AtomLogosVanLanschot class="h-5 lg:h-4 xl:h-5" :alt="logoT_('van-lanschot-kempen')" />
          </AtomLink>
          <AtomLink url="https://www.amsterdamsfondsvoordekunst.nl/" target="_blank" class="md:inline-block shrink-0">
            <AtomLogosAmsterdamsFondsVoorDeKunst class="h-8 lg:h-7 xl:h-8" :alt="logoT_('amsterdams-fonds-voor-de-kunst')" />
          </AtomLink>
          <AtomLink url="https://fondspodiumkunsten.nl/" target="_blank" class="md:inline-block shrink-0">
            <AtomLogosFpk class="h-16 lg:h-12 xl:h-16" :alt="logoT_('fonds-podiumkunsten')" />
          </AtomLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
const navigationStore = useNavigationStore()

const { t_: smT_ } = setupTranslationPrefixer('Footer', 'socialMedia')
const socialMedia = ref([
  {
    icon: 'circled-facebook',
    url: 'https://www.facebook.com/Concertgebouw',
    label: smT_('facebook'),
  },
  {
    icon: 'circled-x',
    url: 'https://x.com/Concertgebouw',
    label: smT_('x'),
  },
  {
    icon: 'circled-spotify',
    url: 'https://open.spotify.com/user/concertgebouw',
    label: smT_('spotify'),
  },
  {
    icon: 'circled-instagram',
    url: 'https://www.instagram.com/concertgebouw/',
    label: smT_('instagram'),
  },
  {
    icon: 'circled-youtube',
    url: 'https://www.youtube.com/user/concertgebouw',
    label: smT_('youtube'),
  },
])

const { t_: logoT_ } = setupTranslationPrefixer('Footer', 'logo')
const { t_ } = setupTranslationPrefixer('Footer', null)

const bottomMenu = computed(() => navigationStore.bottom)
const footerMenu = computed(() => navigationStore.footer)
const newsletterMenu = computed(() => navigationStore.newsletter)
</script>
